import { useMemo, useRef } from 'react';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { useRouter } from 'next/router';
import styled from 'styled-components';

import Office from '../../backend/s3/office';
import Flat from '../../backend/s3/flat';
import Testimonial from '../../backend/testimonial';
import { SEARCH_QUERY_GENERIC, SITE_URL } from '../../common/consts';
import { IOffice } from '../../common/model/office.model';
import { IContact } from '../../common/model/mailchimp/contact.model';
import { IFlat } from '../../common/model/flat.model';
import { ITestimonial } from '../../common/model/testimonial.model';
import { MailchimpInterest } from '../../common/model/mailchimp/enums/mailchimpInterest.enum';
import useI18n from '../../common/hooks/useI18n';
import useMailchimpService from '../../common/hooks/mailchimpService';
import useOnScreen from '../../common/hooks/useOnScreen';
import {
  BuyHouseOption,
  computeBuyHouseOptions,
} from '../../common/helpers/buyHouseOption.utils';
import { getFaqItems } from '../../common/helpers/faq.utils';
import { slugifySearchQuery } from '../../common/helpers/searchQuery.utils';
import { computeResults } from '../../common/helpers/searchService.utils';
import {
  getHighlightedBannerCount,
  computeSellHouseOptions,
} from '../../common/helpers/sellHouse.utils';
import {
  generateFaqStructuredData,
  generateSellHousePageStructuredData,
} from '../../common/helpers/structuredData.utils';

import { LinksGrid, LinksGridProvider } from '../../components/linksGrid';
import {
  Hero,
  HighlightedBanner,
  CardsSection,
  WhySellWithUs,
  FullVideoSection,
  TestimonialsSection,
  FaqSection,
  StickyHighlightedBanner,
} from '../../components/sellHouse';
import { Header, Footer, Head, FlatsDisplay } from '../../components/shared';

type StaticProps = {
  offices: IOffice[];
  buyHouseOptions: BuyHouseOption[];
  serializedFlats: string;
  highlightedBannersCount: number;
  query: string;
  relevantFlats: IFlat[];
  searchOptionInPath: string;
  testimonials: ITestimonial[];
};

type StaticParams = {
  query: string;
};

type Props = StaticProps;

const Layout = styled.div`
  display: flex;
  flex: auto;
  flex-direction: column;
  box-sizing: border-box;
  min-height: 0;

  font-style: ${(props) => props.theme.font.style};
`;

const Content = styled.main`
  position: relative;
  flex: auto;
  margin-top: ${(props) => props.theme.headerHeight};

  @media ${(props) => props.theme.breakpoints.mdd} {
    margin-top: 0;
  }
`;

const OverAffixDiv = styled.div`
  position: relative;
  z-index: 91;
  background-color: white;
`;

const UnderAffixDiv = styled.div`
  position: relative;
  z-index: 89;
`;

const Section = styled.div`
  margin-top: 3rem;
  margin-bottom: 3rem;
  margin-left: ${(props) => props.theme.grid.getGridColumns(2, 1)};
  margin-right: ${(props) => props.theme.grid.getGridColumns(2, 1)};

  @media ${(props) => props.theme.breakpoints.smd} {
    margin-top: 2rem;
    margin-bottom: 2rem;
  }
`;

const FlatsDisplaySection = styled.div`
  margin-top: -3rem;
  margin-bottom: 1rem;

  @media ${(props) => props.theme.breakpoints.smd} {
    margin-top: -2rem;
    margin-bottom: 0;
  }
`;

const SellHousePage: NextPage<Props> = ({
  offices,
  buyHouseOptions,
  serializedFlats,
  highlightedBannersCount,
  query,
  relevantFlats,
  searchOptionInPath,
  testimonials,
}: Props): JSX.Element => {
  const firstOverAffixRef = useRef<HTMLDivElement>(null);
  const secondOverAffixRef = useRef<HTMLDivElement>(null);
  const thirdOverAffixRef = useRef<HTMLDivElement>(null);
  const fourthOverAffixRef = useRef<HTMLDivElement>(null);
  const fifthOverAffixRef = useRef<HTMLDivElement>(null);

  const router = useRouter();
  const i18n = useI18n();
  const mailchimpService = useMailchimpService();

  const structuredData = useMemo(
    () => generateSellHousePageStructuredData(),
    []
  );
  const faqStructuredData = useMemo(
    () => generateFaqStructuredData(getFaqItems(i18n, 'sellHousePage', query)),
    [i18n, query]
  );
  const flats: IFlat[] = useMemo(() => JSON.parse(serializedFlats), [
    serializedFlats,
  ]);

  const isTestimonialsVisible = i18n.t(
    `dynamic.sellHousePage.${query}.testimonialsVideoUrl`
  );

  const refs = useMemo(
    () => [
      firstOverAffixRef,
      secondOverAffixRef,
      thirdOverAffixRef,
      fourthOverAffixRef,
    ],
    []
  );
  if (isTestimonialsVisible) {
    refs.push(fifthOverAffixRef);
  }

  const visibleSectionIndex = useOnScreen(refs);

  const onContactButtonClick = (contact: IContact) => {
    mailchimpService.subscribe(
      { ...contact, interests: [MailchimpInterest.VENDER] },
      router,
      i18n
    );
  };

  return (
    <Layout>
      <Head
        title={i18n.t(`dynamic.sellHousePage.${query}.metaTitle`)}
        description={i18n.t(`dynamic.sellHousePage.${query}.metaDescription`)}
        canonicalUrl={`${SITE_URL}/vender-casa${
          query === SEARCH_QUERY_GENERIC ? '' : '/' + query
        }`}
      >
        {structuredData ? (
          <script
            id="structured-data-script"
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(structuredData),
            }}
          />
        ) : null}
        {faqStructuredData ? (
          <script
            id="structured-data-script"
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(faqStructuredData),
            }}
          />
        ) : null}
      </Head>

      <Header offices={offices} buyHouseOptions={buyHouseOptions} />

      <Content>
        <StickyHighlightedBanner
          query={query}
          visible={visibleSectionIndex < 0}
          onContactButtonClick={onContactButtonClick}
        />

        <OverAffixDiv as="div" ref={firstOverAffixRef} className="first">
          <Hero query={query} onContactButtonClick={onContactButtonClick} />
        </OverAffixDiv>

        <UnderAffixDiv>
          <Section>
            <WhySellWithUs query={query} />
          </Section>
        </UnderAffixDiv>

        <OverAffixDiv as="div" ref={secondOverAffixRef} className="second">
          <HighlightedBanner
            query={query}
            index={0}
            onContactButtonClick={onContactButtonClick}
          />
        </OverAffixDiv>

        <UnderAffixDiv>
          <Section>
            <CardsSection query={query} section="saveYourTime" />
          </Section>

          <Section>
            <FullVideoSection query={query} />
          </Section>
        </UnderAffixDiv>

        <OverAffixDiv as="div" ref={thirdOverAffixRef} className="third">
          <HighlightedBanner
            query={query}
            index={1 % highlightedBannersCount}
            onContactButtonClick={onContactButtonClick}
          />
        </OverAffixDiv>

        <UnderAffixDiv>
          <Section>
            <CardsSection query={query} section="impossibleToSell" />
          </Section>
          <FlatsDisplaySection>
            <FlatsDisplay
              flats={relevantFlats}
              offices={offices}
              title={i18n.t('sellHouse.relevantFlatsTitle', {
                text: searchOptionInPath,
              })}
              newTab
            />
          </FlatsDisplaySection>
        </UnderAffixDiv>

        <OverAffixDiv as="div" ref={fourthOverAffixRef} className="fourth">
          <HighlightedBanner
            query={query}
            index={2 % highlightedBannersCount}
            onContactButtonClick={onContactButtonClick}
          />
        </OverAffixDiv>

        <UnderAffixDiv>
          {isTestimonialsVisible && (
            <Section>
              <TestimonialsSection query={query} testimonials={testimonials} />
            </Section>
          )}
          <Section>
            <FaqSection query={query} />
          </Section>
        </UnderAffixDiv>

        {isTestimonialsVisible && (
          <OverAffixDiv as="div" ref={fifthOverAffixRef} className="fifth">
            <HighlightedBanner
              query={query}
              index={3 % highlightedBannersCount}
              onContactButtonClick={onContactButtonClick}
            />
          </OverAffixDiv>
        )}

        <LinksGridProvider mode="sell" flats={flats} offices={offices}>
          <Section>
            <LinksGrid />
          </Section>
        </LinksGridProvider>
      </Content>

      <Footer />
    </Layout>
  );
};

export const getStaticPaths: GetStaticPaths<StaticParams> = async () => {
  const [_, searchOptions] = await Flat.getFlats();
  const queries = [
    SEARCH_QUERY_GENERIC,
    ...computeSellHouseOptions(searchOptions, true)
      .map((o) => o.query)
      .map((q) => q.replaceAll('/', '--')),
  ];

  const paths = queries.map((query) => ({
    params: {
      query,
    },
  }));

  return { paths, fallback: false };
};

export const getStaticProps: GetStaticProps<
  StaticProps,
  StaticParams
> = async ({ params }) => {
  const offices = await Office.getOffices();
  const [flats, searchOptions] = await Flat.getFlats();

  const buyHouseOptions = computeBuyHouseOptions(searchOptions);
  const highlightedBannersCount = getHighlightedBannerCount(params.query);

  const searchOption = searchOptions.find(
    (s) => slugifySearchQuery(s.text) === params.query
  );
  const searchOptionInPath = searchOption ? searchOption.text : '';
  const relevantFlats = computeResults(flats, {
    county: params.query === SEARCH_QUERY_GENERIC ? undefined : params.query,
  }).slice(0, 4);

  const testimonials = Testimonial.getTestimonials();

  return {
    props: {
      offices,
      buyHouseOptions,
      serializedFlats: JSON.stringify(flats),
      highlightedBannersCount,
      query: params.query.replaceAll('--', '/'),
      relevantFlats,
      searchOptionInPath: searchOptionInPath || 'Inmobiliaria Núcleo',
      testimonials,
    },
  };
};

export default SellHousePage;
